/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import {
  ReactFlowEdge,
  ReactFlowNode,
  TransformEngineNodeHandle,
} from "../Util/TransformEngineDataUtils";
import { Connection } from "@xyflow/react";
import { NODE_TYPES } from "../../../Shared/TransformNodeTypes";
import { loggedNotify, Component } from "../../../Shared/Notify";

/**
 * Result of validating a proposed connection.
 */
export interface RuleValidationResult {
  isValid: boolean;
  errorMessage?: string;
  warningMessage?: string;
  severity?: "error" | "warning" | "info";
}

/**
 * Context needed to validate a proposed connection.
 */
export interface ConnectionRuleContext {
  sourceNode: ReactFlowNode;
  targetNode: ReactFlowNode;
  sourceHandle?: TransformEngineNodeHandle;
  targetHandle?: TransformEngineNodeHandle;
  existingEdges: ReactFlowEdge[];
  allNodes: ReactFlowNode[];
}

/**
 * Connection rules for the transform engine.
 *
 * With transform nodes removed, the only valid edge is
 * input node -> composer node. A composer node may receive many
 * connections, and a single input may feed several of its data members to the
 * same target, so the only connection blocked between a given input and target
 * is an exact duplicate edge (same source and target handle).
 *
 * There are no data-type restrictions today: proto and xml interconvert freely,
 * same-type connections are allowed, and full-file and granular data from the
 * same input can both feed one target. If some conversion later becomes
 * unsupported, add a rule here that rejects that specific case.
 */
export class TransformEngineRules {
  /**
   * Validate a proposed connection against all rules, stopping at the first
   * failure.
   */
  public validateConnection(
    connection: Connection,
    context: ConnectionRuleContext
  ): RuleValidationResult {
    // Rule 1: a node cannot connect to itself.
    const selfConnection = this.checkSelfConnection(connection);
    if (!selfConnection.isValid) return selfConnection;

    // Rule 2: both endpoints must exist.
    const nodesExist = this.checkNodesExist(context);
    if (!nodesExist.isValid) return nodesExist;

    // Rule 3: the exact same edge cannot be created twice.
    const duplicate = this.checkDuplicateConnection(connection, context);
    if (!duplicate.isValid) return duplicate;

    // Rule 4: only input -> composer connections are allowed.
    const topology = this.checkValidTopology(context);
    if (!topology.isValid) return topology;

    return { isValid: true };
  }

  /**
   * Validate a connection and show a notification if it is invalid.
   * @returns whether the connection is valid
   */
  public isConnectionValid(
    connection: Connection,
    context: ConnectionRuleContext,
    axiosContext: any
  ): boolean {
    const result = this.validateConnection(connection, context);

    if (!result.isValid || result.warningMessage) {
      this.emitNotification(result, axiosContext);
    }

    return result.isValid;
  }

  private emitNotification(
    result: RuleValidationResult,
    axiosContext: any
  ): void {
    if (!result.isValid && result.errorMessage) {
      loggedNotify.negative(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        result.errorMessage
      );
    } else if (result.warningMessage) {
      loggedNotify.warning(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        result.warningMessage
      );
    }
  }

  // ==========================================================================
  // RULES
  // ==========================================================================

  private checkSelfConnection(connection: Connection): RuleValidationResult {
    if (connection.source === connection.target) {
      return {
        isValid: false,
        errorMessage: "Cannot connect a node to itself",
        severity: "error",
      };
    }
    return { isValid: true };
  }

  private checkNodesExist(
    context: ConnectionRuleContext
  ): RuleValidationResult {
    if (!context.sourceNode || !context.targetNode) {
      return {
        isValid: false,
        errorMessage: "Source or target node does not exist",
        severity: "error",
      };
    }
    return { isValid: true };
  }

  /**
   * Blocks only an exact duplicate edge (same source handle to same target
   * handle). Different data members of one input feeding the same target are
   * different edges, so they are allowed.
   */
  private checkDuplicateConnection(
    connection: Connection,
    context: ConnectionRuleContext
  ): RuleValidationResult {
    const isDuplicate = context.existingEdges.some(
      (edge) =>
        edge.source === connection.source &&
        edge.target === connection.target &&
        edge.sourceHandle === connection.sourceHandle &&
        edge.targetHandle === connection.targetHandle
    );

    if (isDuplicate) {
      return {
        isValid: false,
        errorMessage: "This connection already exists",
        severity: "error",
      };
    }
    return { isValid: true };
  }

  /**
   * The only valid edge is from an input node to a composer node.
   * This rejects input -> input, anything starting from a composer
   * node, and connections into an input node.
   */
  private checkValidTopology(
    context: ConnectionRuleContext
  ): RuleValidationResult {
    const { sourceNode, targetNode } = context;

    const sourceIsInput = sourceNode.type === NODE_TYPES.input;
    const targetIsComposer = targetNode.type === NODE_TYPES.composer;

    if (!sourceIsInput || !targetIsComposer) {
      return {
        isValid: false,
        errorMessage:
          "Connections must go from an input node to a composer node.",
        severity: "error",
      };
    }

    return { isValid: true };
  }
}
